import { useEffect, useState, type RefObject } from "react";

interface WebkitVideo extends HTMLVideoElement {
  webkitDisplayingFullscreen?: boolean;
}

export function useIsFullscreen(ref: RefObject<HTMLElement | null>): boolean {
  const [isFullscreen, setIsFullscreen] = useState(false);

  /*
    `ref` 는 참조가 안 바뀌므로 이 effect 는 마운트당 한 번만 돈다. 그런데 `<video>` 는
    나중에 붙을 수 있다 — `Media` 는 영상이 있을 때만 ref 를 달고(스냅샷 분기엔 없다),
    상세 시트가 열린 채 이벤트가 바뀌어도 컴포넌트는 리마운트되지 않는다. 그래서
    "스냅샷 이벤트 → (시트 연 채) 영상 이벤트" 순서면 리스너가 하나도 안 붙어
    전체화면 감지가 죽고 가로 전환도 안 걸렸다.

    DOM 변화를 관찰해 `<video>` 가 나타나면 그때 리스너를 붙인다.
  */
  useEffect(() => {
    const el = ref.current;

    const check = () => {
      const box = ref.current;
      if (!box) return setIsFullscreen(false);
      const fsEl = document.fullscreenElement;
      const standard = !!fsEl && (fsEl === box || box.contains(fsEl));
      const video = box.querySelector("video") as WebkitVideo | null;
      setIsFullscreen(standard || !!video?.webkitDisplayingFullscreen);
    };

    let bound: HTMLVideoElement | null = null;
    const bindVideo = () => {
      const video = ref.current?.querySelector("video") ?? null;
      if (video === bound) return;
      bound?.removeEventListener("webkitbeginfullscreen", check);
      bound?.removeEventListener("webkitendfullscreen", check);
      bound = video;
      bound?.addEventListener("webkitbeginfullscreen", check);
      bound?.addEventListener("webkitendfullscreen", check);
    };

    check();
    bindVideo();
    document.addEventListener("fullscreenchange", check);
    // iOS 는 fullscreenchange 를 안 쏘고 video 에만 이벤트를 준다 — 요소 교체를 놓치면 안 된다.
    const mo = el ? new MutationObserver(bindVideo) : null;
    if (el) mo?.observe(el, { childList: true, subtree: true });

    return () => {
      document.removeEventListener("fullscreenchange", check);
      mo?.disconnect();
      bound?.removeEventListener("webkitbeginfullscreen", check);
      bound?.removeEventListener("webkitendfullscreen", check);
    };
  }, [ref]);

  return isFullscreen;
}

type LockableOrientation = {
  lock?: (o: "landscape" | "portrait" | "any") => Promise<void>;
  unlock?: () => void;
};

export function useLandscapeInFullscreen(isFullscreen: boolean, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const o = screen.orientation as unknown as LockableOrientation | undefined;
    if (!o?.lock) return;

    if (!isFullscreen) {
      o.unlock?.();
      return;
    }
    o.lock("landscape").catch(() => {});
    return () => {
      o.unlock?.();
    };
  }, [isFullscreen, enabled]);
}

export function useLandscapeVideo(ref: RefObject<HTMLElement | null>, enabled = true): boolean {
  const isFullscreen = useIsFullscreen(ref);
  useLandscapeInFullscreen(isFullscreen, enabled);
  return isFullscreen;
}
